import type { LegacyChoiceResult } from '@core-api/presentation/types/response';
import type {
  ChoicesT,
  EmptyT,
  InteractiveCanvasComponents,
} from '@mentimeter/question-modules-types';
import { Box } from '@mentimeter/ragnar-ui/box';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import type { MultipleChoiceVisualizationTypeRenderOptions } from '@mentimeter/visualizations-react/types/render-options-by-slide-type/multiple-choice';
import { type PropsWithChildren } from 'react';
import { NonInteractiveVisualizationWrapper } from '../../shared/components/NonInteractiveVisualizationWrapper';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { ScreenReaderTable } from './components/ScreenReaderTable/ScreenReaderTable';
import { BarsDataHandler } from './data-handlers/BarsDataHandler';
import { DotsDataHandler } from './data-handlers/DotsDataHandler';
import { PieDataHandler } from './data-handlers/PieDataHandler';
import { SegmentedBarsDataHandler } from './data-handlers/SegmentedBarsDataHandler';
import { SegmentedDotsDataHandler } from './data-handlers/SegmentedDotsDataHandler';
import type { Choice, Segmentation } from './types';

export interface PresentationModuleProps {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  identifiedResults: LegacyChoiceResult[] | null;
  segmentation: Segmentation;
  shouldShowCorrect: boolean;
  results: ChoicesT | EmptyT;
  choices: Choice[];
  visualizationType: 'bar_chart' | 'donut_chart' | 'pie_chart' | 'dots';
  visualizationTypeRenderOptions?: MultipleChoiceVisualizationTypeRenderOptions;
  shouldHideResults: boolean;
  shouldShowPercent: boolean;
  shouldAnimate: boolean;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
}

function getDisplayVisualizationComponentType({
  amountOfChoices,
  isSegmented,
  visualizationType,
}: {
  amountOfChoices: number;
  isSegmented: boolean;
  visualizationType: PresentationModuleProps['visualizationType'];
}) {
  if (amountOfChoices > 12) {
    return isSegmented ? 'segmented-bars' : 'bars';
  }
  if (
    visualizationType === 'pie_chart' ||
    visualizationType === 'donut_chart'
  ) {
    return 'pie';
  }
  if (visualizationType === 'dots') {
    return 'dots';
  }

  return isSegmented ? 'segmented-bars' : 'bars';
}

export function ChoicesPresentation({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  identifiedResults,
  segmentation,
  shouldShowCorrect,
  results: rawResults,
  choices,
  visualizationType,
  visualizationTypeRenderOptions,
  shouldHideResults,
  shouldShowPercent,
  shouldAnimate,
  CanvasVisualizationArea,
  children,
}: PropsWithChildren<PresentationModuleProps>) {
  const displayVisualizationComponentType =
    getDisplayVisualizationComponentType({
      amountOfChoices: choices.length,
      isSegmented: segmentation.isSegmented,
      visualizationType,
    });

  const resultsWithFallback = choices.map((choice) => {
    const results = rawResults?.results ?? [];
    const result = results.find(({ id }) => id === choice.id);
    const fallbackResult = {
      id: choice.id,
      label: choice.label,
      score: [0],
    };
    return { ...(result ?? fallbackResult), label: choice.label };
  });

  const results = {
    respondents: rawResults?.respondents ?? 0,
    previousRespondents: 0,
    results: resultsWithFallback,
    previousResults: [],
  };

  const ConditionalWrapper =
    CanvasVisualizationArea ?? NonInteractiveVisualizationWrapper;

  return (
    <Box
      width="100%"
      height="100%"
      data-testid="choices-container"
      alignItems="center"
      justifyContent="center"
    >
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />
      <ConditionalWrapper flex="1" width="100%">
        {children || (
          <>
            {displayVisualizationComponentType === 'bars' && (
              <BarsDataHandler
                showCorrect={shouldShowCorrect}
                results={results}
                identifiedResults={identifiedResults}
                shouldShowPercent={shouldShowPercent}
                shouldHideResults={shouldHideResults}
                choices={choices}
                shouldAnimate={shouldAnimate}
                {...visualizationTypeRenderOptions?.barChart}
              />
            )}
            {displayVisualizationComponentType === 'segmented-bars' && (
              <SegmentedBarsDataHandler
                showCorrect={shouldShowCorrect}
                results={results}
                shouldShowPercent={shouldShowPercent}
                shouldHideResults={shouldHideResults}
                choices={choices}
                shouldAnimate={shouldAnimate}
                segmentation={segmentation}
                {...visualizationTypeRenderOptions?.segmentedBarChart}
              />
            )}
            {displayVisualizationComponentType === 'pie' && (
              <PieDataHandler
                showCorrect={shouldShowCorrect}
                results={results}
                identifiedResults={identifiedResults}
                shouldShowPercent={shouldShowPercent}
                shouldHideResults={shouldHideResults}
                choices={choices}
                visualizationType={
                  visualizationType === 'donut_chart' ? 'donut' : 'pie'
                }
                shouldAnimate={shouldAnimate}
                {...visualizationTypeRenderOptions?.pieChart}
              />
            )}
            {displayVisualizationComponentType === 'dots' &&
              (segmentation.isSegmented ? (
                <SegmentedDotsDataHandler
                  showCorrect={shouldShowCorrect}
                  results={results}
                  shouldShowPercent={shouldShowPercent}
                  shouldHideResults={shouldHideResults}
                  choices={choices}
                  shouldAnimate={shouldAnimate}
                  segmentation={segmentation}
                  {...visualizationTypeRenderOptions?.dots}
                />
              ) : (
                <DotsDataHandler
                  showCorrect={shouldShowCorrect}
                  results={results}
                  shouldShowPercent={shouldShowPercent}
                  shouldHideResults={shouldHideResults}
                  identifiedResults={identifiedResults}
                  choices={choices}
                  shouldAnimate={shouldAnimate}
                />
              ))}
            <ScreenReaderOnly>
              <ScreenReaderTable
                choices={choices}
                results={resultsWithFallback}
                shouldShowCorrect={shouldShowCorrect}
                shouldShowScorePercentage={
                  shouldShowPercent
                    ? { percentOf: rawResults?.respondents }
                    : false
                }
                shouldHideResults={shouldHideResults}
              />
            </ScreenReaderOnly>
          </>
        )}
      </ConditionalWrapper>
    </Box>
  );
}
